import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Plus,
  FolderPlus,
  Folder,
  Trash2,
  GripVertical,
  MoreVertical,
  Pencil,
  CircleDot,
  ChevronDown,
  ChevronRight,
  ListChecks,
  ListPlus,
  Lock,
  Eye,
  FolderKanban,
  FolderInput,
  UserRound,
  CheckCircle2,
  Undo2,
} from 'lucide-react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDroppable,
  closestCorners,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  arrayMove,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { ToastAction } from '@/components/ui/toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from '@/components/ui/dropdown-menu';
import type {
  TodoItemStatus,
  TodoItemResponse,
  TodoPersonalItemResponse,
  TodoProjectResponse,
} from '@/lib/api';
import {
  useMyTasksBoard,
  type BoardColumn,
  type BoardEntry,
  type TodoPersonalBoardSyncMode,
} from '../hooks/useMyTasksBoard';
import { useTodoSections, useTodoSubtaskIndex, useTodoProjects } from '../hooks';
import { ProjectChip } from './ProjectChip';
import { SectionPathChip } from './SectionPathChip';
import { resolveProjectColor } from '../lib/projectColors';
import { buildSectionPath } from '../lib/sectionPath';
import { flattenSectionTree } from '../lib/sectionTree';
import { formatItemId } from '../lib/itemId';
import { useSectionCollapse } from '../lib/collapsedSections';
import { parseSearchTerms, taskMatchesTerms } from '../lib/taskSearch';
import { TaskSearchBar } from './TaskSearchBar';
import { STATUS_STYLES, WORK_STATUSES } from '../lib/status';
import { useMultiSelect } from '../hooks/useMultiSelect';
import { SelectionIndicator } from './SelectionIndicator';
import { BulkActionsMenuItems, type BulkActionsProps } from './BulkActionsMenuItems';
import {
  itemContainerId,
  parseItemContainerId,
  reorderSelectedIds,
  makeTodoCollisionDetection,
} from '../lib/dnd';
import { CreatePersonalSectionDialog } from './CreatePersonalSectionDialog';
import { CreatePersonalItemDialog } from './CreatePersonalItemDialog';
import { EditItemDialog } from './EditItemDialog';
import { EditPersonalItemDialog } from './EditPersonalItemDialog';
import { EditPersonalSectionDialog } from './EditPersonalSectionDialog';

// Callbacks threaded down the section tree.
interface ColumnCallbacks {
  board: ReturnType<typeof useMyTasksBoard>;
  childrenByParent: Map<string | null, BoardColumn[]>;
  /** The current list id — keys the persisted collapse state of the section-less
   * "Unsectioned" column, which has no server id of its own. */
  listId: string;
  /** The list's review-required flag — makes the shared-task checkbox a progress
   * marker (In Progress) rather than a direct complete, matching the list view. */
  reviewMode: boolean;
  /** Eligible "send for review" targets; empty disables the card action. */
  reviewerTargets: ReviewerTarget[];
  /** Whether the caller may verify / send back an in-review shared card. */
  canReview: boolean;
  /** Personal sections (flattened, indented) — the "Move to section" targets. */
  boardSectionOptions: Array<{ sectionId: string; name: string; depth: number }>;
  /** Move an entry to a personal column (null = Unsectioned). */
  onMoveEntry: (entry: BoardEntry, personalSectionId: string | null) => void;
  onEditShared: (item: TodoItemResponse) => void;
  onEditPersonal: (item: TodoPersonalItemResponse) => void;
  onEditSection: (sectionId: string) => void;
  onDeleteSection: (sectionId: string) => void;
  /** True when a search query is active — columns filter their entries and hide
   * when they (and their descendants) have no match. */
  searchActive: boolean;
  /** Whether one board entry matches the active search, given its column name. */
  entryMatches: (entry: BoardEntry, sectionName: string | null) => boolean;
  /** projectId → project, for the project chip on each card. */
  projectById: Map<string, TodoProjectResponse>;
  /** Whether to show the project chip on cards in the section view. */
  showProjectOnTasks: boolean;
  /** Whether to show an assigned task's shared-list section path in the section
   *  view (the board's columns are personal, so this is the real "home"). */
  showSectionPathOnTasks: boolean;
  /** entryId → section path (shared for assigned tasks, personal for private). */
  sectionPathByEntryId: Map<string, string[]>;
  /** The list's item-id key (e.g. "FR") for the KEY-N id on a shared card. */
  itemKeyPrefix?: string;
  /** Workspace roster for the shared-task "Assign to" submenu (empty on personal lists). */
  assignableMembers?: Array<{ principalId: string; label: string }>;
  selection?: BoardSelection;
}

/** A "send for review" target — a designated reviewer or an owner/admin. */
interface ReviewerTarget {
  principalId: string;
  label: string;
}

/** Multi-select state + bulk verbs threaded to each card (see BulkActionsMenuItems). */
interface BoardSelection {
  selectedIds: Set<string>;
  count: number;
  onSelectClick: (
    id: string,
    mods: { shiftKey: boolean; metaKey: boolean; ctrlKey: boolean },
  ) => void;
  /** Clear the whole selection (e.g. after a group drag persists). */
  clear: () => void;
  bulkActions: BulkActionsProps;
}

interface MyTasksBoardProps {
  holderId: string | null;
  listId: string;
  /** The list's review-required flag; threaded to the shared-task checkbox. */
  reviewMode?: boolean;
  /** Eligible "send for review" targets (list reviewers + owners/admins). When
   *  non-empty on a review-mode list, assigned shared tasks get a "Send for
   *  review" action, mirroring the List view. */
  reviewerTargets?: ReviewerTarget[];
  /** Whether the caller may create projects (owner/admin). Enables inline
   *  "+ New project" in the editors; members pick from existing projects. */
  canManageProjects?: boolean;
  /** Whether the caller is a designated reviewer of this list — with a manager,
   *  gates the Verify / Send-back controls on in-review shared cards (mirrors the
   *  List view). */
  isListReviewer?: boolean;
  /** Per-list display toggles (default true). */
  showProjectOnTasks?: boolean;
  showSectionPathOnTasks?: boolean;
  /** The list's item-id key (e.g. "FR"); renders the KEY-N id next to a shared
   *  task's checkbox, matching the List view. Undefined lists show no id. */
  itemKeyPrefix?: string;
  /** Workspace roster for the shared-task "Assign to" submenu (empty on personal lists). */
  assignableMembers?: Array<{ principalId: string; label: string }>;
}

// Bounded first-entry auto-sync retries, so a persistent failure can't spin a
// tight retry loop (each attempt is a full board sync).
const MAX_AUTO_SYNC_ATTEMPTS = 3;

export function MyTasksBoard({
  holderId,
  listId,
  reviewMode = false,
  reviewerTargets = [],
  canManageProjects = false,
  isListReviewer = false,
  showProjectOnTasks = true,
  showSectionPathOnTasks = true,
  itemKeyPrefix,
  assignableMembers,
}: MyTasksBoardProps) {
  const board = useMyTasksBoard(holderId, listId);
  // A manager or a designated list reviewer may verify / send back an in-review
  // shared card (mirrors the List view's reviewer affordances).
  const canReview = canManageProjects || isListReviewer;
  // Shared-list sections, so editing an assigned task can move it between them.
  const { sections } = useTodoSections(holderId, listId);
  // Workspace projects: the project picker in the editors, chips on cards, and
  // the group-by-project view. Creating projects is owner/admin server-side.
  const { projects, projectById, createProject } = useTodoProjects(holderId);
  // Local mirror of the columns so drags reflect instantly; re-synced from the
  // server whenever nothing is being dragged.
  const [cols, setCols] = useState<BoardColumn[]>(board.columns);
  const draggingRef = useRef(false);
  const [activeEntry, setActiveEntry] = useState<BoardEntry | null>(null);
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);

  // Add-Section / Add-Item dialogs mirror the shared list's action bar.
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [itemDialogSection, setItemDialogSection] = useState<string | null>(null);
  // The assigned shared task being edited (full editor incl. sub-tasks).
  const [editingShared, setEditingShared] = useState<TodoItemResponse | null>(null);
  // The private personal item being edited (details, section, sub-tasks).
  const [editingPersonal, setEditingPersonal] = useState<TodoPersonalItemResponse | null>(null);
  // Section rename / delete dialogs (by id — details looked up from board.sections).
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);
  const [deletingSectionId, setDeletingSectionId] = useState<string | null>(null);
  // Spinner for the "Sync options" actions. Kept above the loading early return
  // so the hook order never changes between renders.
  const [syncing, setSyncing] = useState(false);
  // Confirm before the destructive "Reset sections".
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  // First-entry auto-sync guard (once per list) — see the effect below.
  const autoSyncedRef = useRef<string | null>(null);
  // Bounded retry budget for the auto-sync, reset per list. Bumping the tick
  // state re-runs the effect (a ref mutation alone would not re-render).
  const autoSyncAttemptsRef = useRef(0);
  const [autoSyncTick, setAutoSyncTick] = useState(0);
  // The live listId + mounted flag, read by the async sync callback to ignore a
  // rejection that belongs to a list we've navigated away from (or an unmounted
  // component) — otherwise a stale failure would burn the new list's retry budget
  // or setState after unmount. A cancellation flag can't be used here because
  // `board.syncBoard`'s identity changes each render, so the effect (and its
  // cleanup) re-runs constantly, which would cancel every in-flight attempt.
  const autoSyncListIdRef = useRef(listId);
  const autoSyncMountedRef = useRef(true);
  const { toast } = useToast();
  // Free-text search across each task's loaded content (title, description,
  // status, section, and the "belongs to X" displaced note).
  const [searchQuery, setSearchQuery] = useState('');
  const searchTerms = parseSearchTerms(searchQuery);
  const searchActive = searchTerms.length > 0;
  // Group the board by section (default) or by project.
  const [groupBy, setGroupBy] = useState<'section' | 'project'>('section');
  // Assigned shared tasks are searchable by their sub-tasks' text (via the shared
  // list's sub-task index). Private tasks have no sub-tasks — only a checklist.
  const sharedSubtaskText = useTodoSubtaskIndex(holderId, listId, searchActive);
  const entryMatches = (entry: BoardEntry, sectionName: string | null): boolean =>
    taskMatchesTerms(
      {
        title: entry.title,
        description: entry.personal?.description ?? entry.shared?.description ?? null,
        statusLabel: STATUS_STYLES[entry.status]?.label,
        sectionName,
        extra: [
          entry.displacedFromSection,
          entry.kind === 'shared' ? sharedSubtaskText.get(entry.id) : undefined,
        ],
      },
      searchTerms,
    );

  useEffect(() => {
    if (!draggingRef.current) setCols(board.columns);
  }, [board.columns]);

  // Track the live listId + reset the retry budget when switching lists.
  useEffect(() => {
    autoSyncListIdRef.current = listId;
    autoSyncAttemptsRef.current = 0;
  }, [listId]);
  useEffect(() => {
    autoSyncMountedRef.current = true;
    return () => {
      autoSyncMountedRef.current = false;
    };
  }, []);

  // First entry: a board that's never been synced auto-mirrors the shared list's
  // sections so the user starts organized — no manual "match" step. Keyed on the
  // durable `hasSynced` flag (set by the sync op), so emptying the board later to
  // keep a flat list never re-creates the sections. The ref prevents a
  // double-fire within a single mount while the first sync is in flight.
  useEffect(() => {
    if (board.loading || autoSyncedRef.current === listId) return;
    if (autoSyncAttemptsRef.current >= MAX_AUTO_SYNC_ATTEMPTS) return; // budget spent
    if (board.hasSynced || sections.length === 0) return;
    const attemptListId = listId;
    autoSyncedRef.current = listId;
    void board.syncBoard('add_missing').catch(() => {
      // Ignore a rejection from a list we've since left, or after unmount.
      if (!autoSyncMountedRef.current || attemptListId !== autoSyncListIdRef.current) return;
      // Transient failure: clear the in-flight guard and, within a small attempt
      // budget, bump state so this effect re-runs and retries. A ref mutation
      // alone wouldn't re-render, so the retry would never fire.
      if (autoSyncedRef.current === attemptListId) autoSyncedRef.current = null;
      autoSyncAttemptsRef.current += 1;
      if (autoSyncAttemptsRef.current < MAX_AUTO_SYNC_ATTEMPTS) {
        setAutoSyncTick((t) => t + 1);
      } else {
        // Budget exhausted — one non-repeating nudge to the manual Sync options
        // (which report their own failures). The attempts guard above stops any
        // further auto attempts for this list.
        toast({
          variant: 'destructive',
          title: "Couldn't set up your board",
          description: 'Auto-sync didn’t go through. Use “Sync options” to match the list.',
        });
      }
    });
  }, [
    board.loading,
    board.hasSynced,
    sections.length,
    listId,
    board.syncBoard,
    autoSyncTick,
    toast,
  ]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  const unsectionedCol = cols.find((c) => c.personalSectionId === null) ?? null;
  const sectionCols = cols.filter((c) => c.personalSectionId !== null);
  const sectionIds = sectionCols.map((c) => c.personalSectionId as string);

  // Each entry's section path for the section-path chip in the project view. An
  // ASSIGNED shared task shows its true home in the SHARED list (deep even if
  // your board is flat); a PRIVATE board item shows its personal-section path
  // (the column it sits in — it has no shared section).
  const personalSectionById = new Map(board.sections.map((s) => [s.personalSectionId, s]));
  const sharedSectionById = new Map(sections.map((s) => [s.sectionId, s]));
  const sectionPathByEntryId = new Map<string, string[]>();
  for (const c of cols) {
    const personalPath = buildSectionPath(c.personalSectionId, (id) => {
      const s = personalSectionById.get(id);
      return s ? { name: s.name, parentId: s.parentPersonalSectionId } : undefined;
    });
    for (const e of c.entries) {
      if (e.kind === 'shared' && e.shared) {
        sectionPathByEntryId.set(
          e.id,
          buildSectionPath(e.shared.sectionId, (id) => {
            const s = sharedSectionById.get(id);
            return s ? { name: s.name, parentId: s.parentSectionId } : undefined;
          }),
        );
      } else {
        sectionPathByEntryId.set(e.id, personalPath);
      }
    }
  }

  // Group real sections by parent to render the tree.
  const childrenByParent = new Map<string | null, BoardColumn[]>();
  for (const c of sectionCols) {
    const p = c.parentPersonalSectionId ?? null;
    const arr = childrenByParent.get(p) ?? [];
    arr.push(c);
    childrenByParent.set(p, arr);
  }
  const parentOf = (id: string): string | null =>
    sectionCols.find((c) => c.personalSectionId === id)?.parentPersonalSectionId ?? null;
  const siblingIdsOf = (id: string): string[] =>
    (childrenByParent.get(parentOf(id)) ?? []).map((c) => c.personalSectionId as string);

  // --- Search visibility. DnD keeps using the full `childrenByParent` above;
  // search only prunes what's DISPLAYED. A section column is shown when it or any
  // descendant holds a matching entry; `displayChildrenByParent` is the pruned
  // tree handed to the render (via callbacks), leaving drag logic untouched.
  const columnVisible = new Set<string | null>();
  const computeColumnVisible = (sectionId: string | null): boolean => {
    const kids = childrenByParent.get(sectionId) ?? [];
    let anyChildVisible = false;
    for (const k of kids) {
      if (computeColumnVisible(k.personalSectionId)) anyChildVisible = true;
    }
    const col = sectionCols.find((c) => c.personalSectionId === sectionId);
    const selfMatch = !!col && col.entries.some((e) => entryMatches(e, col.name));
    const visible = selfMatch || anyChildVisible;
    if (visible) columnVisible.add(sectionId);
    return visible;
  };
  if (searchActive) {
    for (const root of childrenByParent.get(null) ?? [])
      computeColumnVisible(root.personalSectionId);
  }
  const unsectionedVisible =
    !searchActive ||
    (!!unsectionedCol && unsectionedCol.entries.some((e) => entryMatches(e, unsectionedCol.name)));
  const displayChildrenByParent = searchActive
    ? new Map(
        [...childrenByParent].map(([parent, kids]) => [
          parent,
          kids.filter((k) => columnVisible.has(k.personalSectionId)),
        ]),
      )
    : childrenByParent;
  const matchCount = searchActive
    ? cols.reduce((n, c) => n + c.entries.filter((e) => entryMatches(e, c.name)).length, 0)
    : 0;
  const hasAnyMatch = unsectionedVisible || (displayChildrenByParent.get(null)?.length ?? 0) > 0;

  // Two drag modes share one context — the same strategy the shared List page
  // uses (see makeTodoCollisionDetection in lib/dnd).
  const collisionDetection = makeTodoCollisionDetection({
    siblingSectionIdsOf: siblingIdsOf,
    allSectionIds: new Set(sectionIds),
  });

  const findColIndex = (entryId: string) =>
    cols.findIndex((c) => c.entries.some((e) => e.id === entryId));
  const findColIndexByContainer = (overId: string) => {
    const parsed = parseItemContainerId(overId);
    if (parsed.isContainer) {
      return cols.findIndex((c) => (c.personalSectionId ?? null) === parsed.sectionId);
    }
    const asSection = cols.findIndex((c) => c.personalSectionId === overId);
    if (asSection !== -1) return asSection;
    return findColIndex(overId);
  };

  function handleDragStart(e: DragStartEvent) {
    draggingRef.current = true;
    if (e.active.data.current?.type === 'section') {
      setActiveSectionId(String(e.active.id));
      return;
    }
    const id = String(e.active.id);
    for (const c of cols) {
      const entry = c.entries.find((x) => x.id === id);
      if (entry) {
        setActiveEntry(entry);
        return;
      }
    }
  }

  function handleDragOver(e: DragOverEvent) {
    if (e.active.data.current?.type === 'section') return; // sections reorder on drop
    const activeId = String(e.active.id);
    const overId = e.over ? String(e.over.id) : null;
    if (!overId) return;
    const from = findColIndex(activeId);
    const to = findColIndexByContainer(overId);
    if (from === -1 || to === -1 || from === to) return;
    setCols((prev) => {
      const next = prev.map((c) => ({ ...c, entries: [...c.entries] }));
      const fromEntries = next[from].entries;
      const idx = fromEntries.findIndex((x) => x.id === activeId);
      if (idx === -1) return prev;
      const [moved] = fromEntries.splice(idx, 1);
      next[to].entries.push(moved);
      return next;
    });
  }

  function handleSectionDragEnd(e: DragEndEvent) {
    setActiveSectionId(null);
    draggingRef.current = false;
    const activeId = String(e.active.id);
    const overId = e.over ? String(e.over.id) : null;
    if (!overId || overId === activeId) return;
    const sibs = siblingIdsOf(activeId);
    const oldIndex = sibs.indexOf(activeId);
    const newIndex = sibs.indexOf(overId);
    if (oldIndex === -1 || newIndex === -1 || oldIndex === newIndex) return;
    const newIds = arrayMove(sibs, oldIndex, newIndex);
    // Optimistically reorder the sibling slots within cols; other columns keep
    // their positions. Then persist (server sets sort_order = index per sibling).
    const bySection = new Map(sectionCols.map((c) => [c.personalSectionId as string, c]));
    const sibSet = new Set(newIds);
    let cursor = 0;
    setCols(
      cols.map((c) =>
        c.personalSectionId && sibSet.has(c.personalSectionId)
          ? bySection.get(newIds[cursor++])!
          : c,
      ),
    );
    board.reorderSections(newIds);
  }

  function handleDragEnd(e: DragEndEvent) {
    if (e.active.data.current?.type === 'section') {
      handleSectionDragEnd(e);
      return;
    }
    draggingRef.current = false;
    setActiveEntry(null);
    const activeId = String(e.active.id);
    const overId = e.over ? String(e.over.id) : null;
    const to = overId ? findColIndexByContainer(overId) : findColIndex(activeId);
    if (to === -1) {
      setCols(board.columns);
      return;
    }
    // Multi-drag: dropping a SELECTED card moves the WHOLE selection. If every
    // selected card is already in the destination column it's a REORDER — slide the
    // selected block to the drop position; otherwise it's a cross-column MOVE. An
    // UNSELECTED card falls through to the normal single-card path below.
    if (selectionCount > 1 && selectedIds.has(activeId)) {
      const destCol = cols[to];
      const destIds = new Set(destCol.entries.map((en) => en.id));
      const sameColumn = selectedEntries.every((en) => destIds.has(en.id));
      if (sameColumn) {
        const ids = destCol.entries.map((en) => en.id);
        const overEntryId = destCol.entries.some((en) => en.id === overId) ? overId : null;
        const byId = new Map(destCol.entries.map((en) => [en.id, en]));
        const reordered = reorderSelectedIds(ids, selectedIds, activeId, overEntryId).map(
          (id) => byId.get(id)!,
        );
        setCols(cols.map((c, i) => (i === to ? { ...c, entries: reordered } : c)));
        board.reorderEntries(
          destCol.personalSectionId,
          reordered.map((en) => ({ kind: en.kind, id: en.id })),
        );
      } else {
        // Cross-column move. Relocate only the selected cards NOT already in the
        // destination column (re-moving present ones would shove them to the
        // bottom), and place all moved SHARED tasks in ONE reorder so their sort
        // positions don't collide. Personal items are independent rows.
        const moved = selectedEntries.filter((entry) => !destIds.has(entry.id));
        const movedShared = moved.filter((e) => e.kind === 'shared');
        const movedPersonal = moved.filter((e) => e.kind === 'personal');
        if (movedShared.length) {
          const refs = [
            ...destCol.entries
              .filter((e) => !movedShared.some((m) => m.id === e.id))
              .map((e) => ({ kind: e.kind, id: e.id })),
            ...movedShared.map((e) => ({ kind: 'shared' as const, id: e.id })),
          ];
          board.reorderEntries(destCol.personalSectionId, refs);
        }
        movedPersonal.forEach((e) =>
          board.updatePersonalItem(e.id, { personalSectionId: destCol.personalSectionId }),
        );
      }
      clearSelection();
      return;
    }
    let finalCols = cols;
    const destEntries = cols[to].entries;
    const oldIndex = destEntries.findIndex((x) => x.id === activeId);
    const overIndex = overId ? destEntries.findIndex((x) => x.id === overId) : -1;
    if (oldIndex !== -1 && overIndex !== -1 && oldIndex !== overIndex) {
      finalCols = cols.map((c, i) =>
        i === to ? { ...c, entries: arrayMove(c.entries, oldIndex, overIndex) } : c,
      );
      setCols(finalCols);
    }
    const destCol = finalCols[to];
    board.reorderEntries(
      destCol.personalSectionId,
      destCol.entries.map((en) => ({ kind: en.kind, id: en.id })),
    );
  }

  function handleDragCancel() {
    draggingRef.current = false;
    setActiveEntry(null);
    setActiveSectionId(null);
    setCols(board.columns);
  }

  function openItemDialog(sectionId: string | null) {
    setItemDialogSection(sectionId);
    setItemDialogOpen(true);
  }

  // ---- Multi-select HOOKS — must run before the loading early-return below so
  // the hook order never changes between the loading and loaded renders. The
  // non-hook bulk-action derivations live further down (they need moveEntryToColumn
  // / movableBoardSections, which are defined after this return).
  const orderedEntryIds = useMemo(() => cols.flatMap((c) => c.entries.map((e) => e.id)), [cols]);
  const {
    selectedIds,
    count: selectionCount,
    clear: clearSelection,
    retain: retainSelection,
    handleSelectClick,
  } = useMultiSelect(orderedEntryIds);
  useEffect(() => {
    retainSelection(new Set(orderedEntryIds));
  }, [orderedEntryIds, retainSelection]);
  // Esc / click-off dismissal is owned by useMultiSelect (shared with the List view).
  const selectedEntries = useMemo(
    () => cols.flatMap((c) => c.entries).filter((e) => selectedIds.has(e.id)),
    [cols, selectedIds],
  );

  if (board.loading) {
    return <p className="text-sm text-muted-foreground">Loading your board…</p>;
  }

  const activeSectionName = activeSectionId
    ? (cols.find((c) => c.personalSectionId === activeSectionId)?.name ?? null)
    : null;
  const editingSection = editingSectionId
    ? (board.sections.find((s) => s.personalSectionId === editingSectionId) ?? null)
    : null;
  const deletingName = deletingSectionId
    ? (cols.find((c) => c.personalSectionId === deletingSectionId)?.name ?? '')
    : '';

  // The shared-task editor lists PERSONAL board sections (adapted to the picker's
  // four-field shape) — in My Tasks, "Section" means which personal column the
  // task sits in, which can differ from its shared-list home.
  const personalSectionOptions = board.sections.map((s) => ({
    sectionId: s.personalSectionId,
    parentSectionId: s.parentPersonalSectionId,
    sortOrder: s.sortOrder,
    name: s.name,
  }));
  // The personal column the task being edited currently sits in (its placement,
  // name-matched home, or the unsectioned inbox) — the picker starts here.
  const editingSharedColumnId = editingShared
    ? (cols.find((c) => c.entries.some((e) => e.kind === 'shared' && e.id === editingShared.itemId))
        ?.personalSectionId ?? null)
    : null;

  // Move an assigned shared task into a personal column, mirroring a drag: append
  // it to that column and persist the column's entry order. The placement is an
  // upsert keyed by (principal, item), so this also clears its old column.
  function moveSharedEntryToColumn(itemId: string, destPersonalSectionId: string | null) {
    const destCol = cols.find((c) => (c.personalSectionId ?? null) === destPersonalSectionId);
    const refs = [
      ...(destCol?.entries ?? [])
        .filter((e) => e.id !== itemId)
        .map((e) => ({ kind: e.kind, id: e.id })),
      { kind: 'shared' as const, id: itemId },
    ];
    board.reorderEntries(destPersonalSectionId, refs);
  }

  // Move ANY board entry to a personal column (the "Move to section" quick
  // action): a private item updates its own section, an assigned shared task
  // gets a placement (via moveSharedEntryToColumn).
  function moveEntryToColumn(entry: BoardEntry, destPersonalSectionId: string | null) {
    if (entry.kind === 'personal') {
      board.updatePersonalItem(entry.id, { personalSectionId: destPersonalSectionId });
    } else {
      moveSharedEntryToColumn(entry.id, destPersonalSectionId);
    }
  }
  // Personal sections flattened (indented by depth) for the move menu — reuses
  // the same {sectionId, parentSectionId, sortOrder, name} shape as the editor
  // picker above (personalSectionOptions), just flattened.
  const movableBoardSections = flattenSectionTree(personalSectionOptions).map(
    ({ section, depth }) => ({ sectionId: section.sectionId, name: section.name, depth }),
  );

  // ---- Bulk-action derivations (non-hook) ----------------------------------
  // The selection HOOKS run above the loading early-return; these plain consts
  // build the bulk verbs from the selection. They adapt to the selection's
  // composition: Assign to / Send for review need an all-shared selection, Delete
  // an all-private one, while Set status / Set project and Move to section work on
  // any mix.
  const allShared = selectedEntries.length > 0 && selectedEntries.every((e) => e.kind === 'shared');
  const allPersonal =
    selectedEntries.length > 0 && selectedEntries.every((e) => e.kind === 'personal');
  const selectionReviewReady =
    allShared &&
    selectedEntries.every(
      (e) =>
        (e.shared?.done ?? false) &&
        (e.shared?.subtaskTotal ?? 0) <= (e.shared?.subtaskDone ?? 0) &&
        (e.shared?.checklistTotal ?? 0) <= (e.shared?.checklistDone ?? 0),
    );
  // Apply a mutation to every selected card, then clear + toast a summary that
  // surfaces partial failures — mirrors the List view's runBulk. Each `fn`
  // returns the mutation promise (the board's *Async twins) so a failed request
  // is counted, not silently reported as success.
  const runBulkBoard = async (verb: string, fn: (entry: BoardEntry) => Promise<unknown>) => {
    const entries = selectedEntries;
    if (!entries.length) return;
    // Suppress the per-request error toasts while aggregating — the summary below
    // is the single source of truth for a bulk action's outcome.
    const results = await board.withBulkErrorSuppressed(() =>
      Promise.allSettled(entries.map((e) => fn(e))),
    );
    const failed = results.filter((r) => r.status === 'rejected').length;
    const ok = entries.length - failed;
    clearSelection();
    if (failed === 0) {
      toast({ title: `${verb} ${ok} task${ok === 1 ? '' : 's'}` });
    } else {
      toast({
        title: `${verb} ${ok} of ${entries.length}`,
        description: `${failed} couldn't be updated.`,
        variant: 'destructive',
      });
    }
  };
  // Bulk "Move to section" is special: the shared placements MUST be ONE atomic
  // reorder (destination's current order + all moved shared tasks appended in
  // selection order). Issuing one reorder per task races on a stale client
  // snapshot and collides their sort positions (all land "last"). Personal items
  // are independent rows, so they update per item.
  const runBulkSectionMove = async (destPersonalSectionId: string | null) => {
    const entries = selectedEntries;
    if (!entries.length) return;
    const destCol = cols.find((c) => (c.personalSectionId ?? null) === destPersonalSectionId);
    const destIds = new Set((destCol?.entries ?? []).map((e) => e.id));
    const movedShared = entries.filter((e) => e.kind === 'shared' && !destIds.has(e.id));
    const personal = entries.filter((e) => e.kind === 'personal');
    // Each op carries the number of TASKS it covers so the summary counts tasks,
    // not requests (the shared reorder is one request for N tasks).
    const ops: Array<{ tasks: number; run: () => Promise<unknown> }> = [];
    if (movedShared.length) {
      const refs = [
        ...(destCol?.entries ?? [])
          .filter((e) => !movedShared.some((m) => m.id === e.id))
          .map((e) => ({ kind: e.kind, id: e.id })),
        ...movedShared.map((e) => ({ kind: 'shared' as const, id: e.id })),
      ];
      ops.push({
        tasks: movedShared.length,
        run: () => board.reorderEntriesAsync(destPersonalSectionId, refs),
      });
    }
    for (const p of personal) {
      ops.push({
        tasks: 1,
        run: () =>
          board.updatePersonalItemAsync(p.id, { personalSectionId: destPersonalSectionId }),
      });
    }
    const results = await board.withBulkErrorSuppressed(() =>
      Promise.allSettled(ops.map((o) => o.run())),
    );
    let failedTasks = 0;
    results.forEach((r, i) => {
      if (r.status === 'rejected') failedTasks += ops[i].tasks;
    });
    const okTasks = entries.length - failedTasks;
    clearSelection();
    if (failedTasks === 0) {
      toast({ title: `Moved ${okTasks} task${okTasks === 1 ? '' : 's'}` });
    } else {
      toast({
        title: `Moved ${okTasks} of ${entries.length}`,
        description: `${failedTasks} couldn't be moved.`,
        variant: 'destructive',
      });
    }
  };
  const bulkActions: BulkActionsProps = {
    members: allShared ? assignableMembers : undefined,
    onAssign: allShared
      ? (p) =>
          runBulkBoard(p ? 'Assigned' : 'Unassigned', (e) =>
            p ? board.assignSharedAsync(e.id, p) : board.unassignSharedAsync(e.id),
          )
      : undefined,
    statusOptions: WORK_STATUSES,
    onSetStatus: (s) =>
      runBulkBoard('Updated', (e) =>
        e.kind === 'shared'
          ? board.setSharedStatusAsync(e.id, s as TodoItemStatus)
          : board.setPersonalItemStatusAsync(e.id, s as TodoItemStatus),
      ),
    projects,
    onSetProject: (pid) =>
      runBulkBoard('Updated', (e) =>
        e.kind === 'shared'
          ? board.updateSharedItemAsync(e.id, { projectId: pid })
          : board.updatePersonalItemAsync(e.id, { projectId: pid }),
      ),
    sectionOptions: movableBoardSections,
    onSetSection: (sid) => runBulkSectionMove(sid),
    reviewerTargets: allShared && reviewMode ? reviewerTargets : undefined,
    reviewReady: selectionReviewReady,
    reviewBlockReason:
      'All selected tasks must have their checklist and sub-tasks completed first.',
    onSendForReview:
      allShared && reviewMode
        ? (rid) => runBulkBoard('Sent for review', (e) => board.requestSharedReviewAsync(e.id, rid))
        : undefined,
    onDelete: allPersonal
      ? () => runBulkBoard('Deleted', (e) => board.deletePersonalItemAsync(e.id))
      : undefined,
  };
  const boardSelection: BoardSelection = {
    selectedIds,
    count: selectionCount,
    onSelectClick: handleSelectClick,
    clear: clearSelection,
    bulkActions,
  };

  async function handleSync(mode: TodoPersonalBoardSyncMode) {
    setSyncing(true);
    try {
      await board.syncBoard(mode);
    } catch {
      // A manual sync the user explicitly triggered — surface the failure so
      // they can retry, rather than swallowing it into an unhandled rejection.
      toast({
        variant: 'destructive',
        title: "Couldn't sync your board",
        description: 'Something went wrong syncing to the list. Please try again.',
      });
    } finally {
      setSyncing(false);
    }
  }
  // Sync options are only meaningful when the shared list actually has sections.
  const canSync = sections.length > 0;

  const callbacks: ColumnCallbacks = {
    board,
    // Pruned tree while searching so empty branches don't render; DnD still uses
    // the full childrenByParent above.
    childrenByParent: displayChildrenByParent,
    listId,
    reviewMode,
    reviewerTargets,
    canReview,
    boardSectionOptions: movableBoardSections,
    onMoveEntry: moveEntryToColumn,
    onEditShared: setEditingShared,
    onEditPersonal: setEditingPersonal,
    onEditSection: setEditingSectionId,
    onDeleteSection: setDeletingSectionId,
    searchActive,
    entryMatches,
    projectById,
    showProjectOnTasks,
    showSectionPathOnTasks,
    sectionPathByEntryId,
    itemKeyPrefix,
    assignableMembers,
    selection: boardSelection,
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        Your private view — only you see it, and it never changes the shared list.
      </p>

      {/* Action bar. "Add Personal" creates a PRIVATE task tracked only on your
          board (not on the shared list). Sub-tasks come from a task's "+S", not
          from here — and personal tasks have no "+S". */}
      <div className="flex flex-wrap items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => setSectionDialogOpen(true)}>
          <FolderPlus className="mr-2 h-4 w-4" />
          Add Section
        </Button>
        <Button variant="outline" size="sm" onClick={() => openItemDialog(null)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Personal
        </Button>
        {canSync && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" disabled={syncing}>
                <Folder className="mr-2 h-4 w-4" />
                {syncing ? 'Syncing…' : 'Sync options'}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="max-w-[18rem]">
              {/* Ordered least → most invasive. */}
              <DropdownMenuItem
                className="flex-col items-start gap-0.5"
                onClick={() => handleSync('add_missing')}
              >
                <span className="font-medium">Add missing sections</span>
                <span className="text-xs text-muted-foreground">
                  Create any shared-list sections you don&rsquo;t have yet. Nothing is moved or
                  removed.
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="flex-col items-start gap-0.5"
                onClick={() => handleSync('items_and_structure')}
              >
                <span className="font-medium">Sync items and structure</span>
                <span className="text-xs text-muted-foreground">
                  Add missing sections and move your tasks to their shared-list sections. Keeps your
                  custom sections.
                </span>
              </DropdownMenuItem>
              <DropdownMenuItem
                className="flex-col items-start gap-0.5 text-destructive focus:text-destructive"
                onClick={() => setResetConfirmOpen(true)}
              >
                <span className="font-medium">Reset sections</span>
                <span className="text-xs text-muted-foreground">
                  Mirror the shared list exactly and remove your custom sections. Your private tasks
                  move to Unsectioned.
                </span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {/* Search + the Group-by toggle, matching the List view. */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <TaskSearchBar
          value={searchQuery}
          onChange={setSearchQuery}
          resultCount={searchActive ? matchCount : undefined}
        />
        <div className="inline-flex items-center gap-0.5 rounded-lg bg-muted p-0.5">
          {(['section', 'project'] as const).map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGroupBy(g)}
              aria-pressed={groupBy === g}
              className={
                groupBy === g
                  ? 'rounded-md bg-background px-3 py-1 text-sm font-medium text-foreground shadow-sm'
                  : 'rounded-md px-3 py-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground'
              }
            >
              {g === 'section' ? 'Sections' : 'Projects'}
            </button>
          ))}
        </div>
      </div>

      <SelectionIndicator count={selectionCount} onClear={clearSelection} />

      {groupBy === 'project' && (
        <BoardProjectView
          entries={cols.flatMap((c) => c.entries)}
          projects={projects}
          projectById={projectById}
          board={board}
          reviewMode={reviewMode}
          reviewerTargets={reviewerTargets}
          canReview={canReview}
          boardSectionOptions={movableBoardSections}
          onMoveEntry={moveEntryToColumn}
          sectionPathByEntryId={sectionPathByEntryId}
          showSectionPath={showSectionPathOnTasks}
          itemKeyPrefix={itemKeyPrefix}
          assignableMembers={assignableMembers}
          selection={boardSelection}
          onEditShared={setEditingShared}
          onEditPersonal={setEditingPersonal}
        />
      )}

      {groupBy === 'section' && (
        <DndContext
          sensors={sensors}
          collisionDetection={collisionDetection}
          onDragStart={handleDragStart}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={handleDragCancel}
        >
          <div className="space-y-4">
            {unsectionedCol && unsectionedVisible && (
              <UnsectionedColumnCard col={unsectionedCol} cb={callbacks} />
            )}
            <SectionTree parentId={null} depth={0} cb={callbacks} />
            {searchActive && !hasAnyMatch && (
              <p className="py-8 text-center text-sm text-muted-foreground">
                No tasks match your search.
              </p>
            )}
          </div>
          <DragOverlay>
            {activeEntry ? (
              <div className="rounded-md border bg-background px-3 py-2 text-sm shadow-lg">
                {selectionCount > 1 && selectedIds.has(activeEntry.id)
                  ? `${selectionCount} tasks`
                  : activeEntry.title}
              </div>
            ) : activeSectionName ? (
              <div className="rounded-lg border bg-background px-3 py-2 text-sm font-medium shadow-lg">
                {activeSectionName}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      <CreatePersonalSectionDialog
        open={sectionDialogOpen}
        onClose={() => setSectionDialogOpen(false)}
        onCreate={(input) => board.createSection(input)}
        sections={board.sections}
      />
      <CreatePersonalItemDialog
        open={itemDialogOpen}
        onClose={() => setItemDialogOpen(false)}
        onCreate={(input) => board.createItem(input)}
        sections={board.sections}
        defaultSectionId={itemDialogSection}
      />
      <EditItemDialog
        open={!!editingShared}
        onClose={() => setEditingShared(null)}
        item={editingShared}
        // The board's "Section" is the caller's PERSONAL column, not the shared
        // list's — those two section sets can differ. List personal sections and
        // start on the task's current column.
        sections={personalSectionOptions}
        initialSectionId={editingSharedColumnId}
        sectionNoneLabel="Unsectioned"
        holderId={holderId}
        projects={projects}
        onCreateProject={canManageProjects ? (name) => createProject({ name }) : undefined}
        // A task shown here is assigned to the caller, so they fully own it: the
        // server's assignee-edit path lets them retarget its Project and manage its
        // sub-tasks. Give them the same full editor the List view gives the assignee.
        canEditStructure={true}
        onSave={(itemId, { sectionId, ...shared }) => {
          // A Section change moves the task within the personal board (a per-user
          // placement) — never a shared-list re-file. Title / description / project
          // remain shared edits.
          if (sectionId !== undefined) moveSharedEntryToColumn(itemId, sectionId);
          board.updateSharedItem(itemId, shared);
        }}
      />
      <EditPersonalItemDialog
        open={!!editingPersonal}
        onClose={() => setEditingPersonal(null)}
        item={editingPersonal}
        sections={board.sections}
        holderId={holderId}
        listId={listId}
        projects={projects}
        onCreateProject={canManageProjects ? (name) => createProject({ name }) : undefined}
        onSave={(id, input) => board.updatePersonalItem(id, input)}
      />
      <EditPersonalSectionDialog
        open={!!editingSection}
        onClose={() => setEditingSectionId(null)}
        section={editingSection}
        sections={board.sections}
        onSave={(id, input) => board.updateSection(id, input)}
      />

      <Dialog open={!!deletingSectionId} onOpenChange={(o) => !o && setDeletingSectionId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Delete Section</DialogTitle>
            <DialogDescription>
              Delete &ldquo;{deletingName}&rdquo;? Its items move to Unsectioned and any nested
              sections are removed — the shared list is unaffected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeletingSectionId(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (deletingSectionId) board.deleteSection(deletingSectionId);
                setDeletingSectionId(null);
              }}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={resetConfirmOpen} onOpenChange={(o) => !o && setResetConfirmOpen(false)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Reset sections?</DialogTitle>
            <DialogDescription>
              This mirrors the shared list exactly: your custom sections are removed and your
              assigned tasks move to their shared-list sections. Your private tasks aren&rsquo;t
              deleted — they move to Unsectioned. The shared list is unaffected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                setResetConfirmOpen(false);
                void handleSync('reset');
              }}
            >
              Reset sections
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Renders the sibling sections under `parentId`, each in one SortableContext. */
function SectionTree({
  parentId,
  depth,
  cb,
}: {
  parentId: string | null;
  depth: number;
  cb: ColumnCallbacks;
}) {
  const siblings = cb.childrenByParent.get(parentId) ?? [];
  if (siblings.length === 0) return null;
  return (
    <SortableContext
      items={siblings.map((s) => s.personalSectionId as string)}
      strategy={verticalListSortingStrategy}
    >
      <div className={depth > 0 ? 'space-y-3' : 'space-y-4'}>
        {siblings.map((col) => (
          <SectionColumnCard key={col.personalSectionId} col={col} depth={depth} cb={cb} />
        ))}
      </div>
    </SortableContext>
  );
}

function ColumnHeader({
  name,
  expanded,
  onToggleExpanded,
  sectionId,
  onEditSection,
  onDeleteSection,
  dragHandle,
}: {
  name: string;
  expanded: boolean;
  onToggleExpanded: () => void;
  sectionId: string | null;
  onEditSection?: (id: string) => void;
  onDeleteSection?: (id: string) => void;
  dragHandle?: ReactNode;
}) {
  return (
    <div className="flex items-center gap-1">
      {dragHandle}
      <button
        type="button"
        onClick={onToggleExpanded}
        className="flex-shrink-0 text-muted-foreground hover:text-foreground"
        aria-label={`${expanded ? 'Collapse' : 'Expand'} section "${name}"`}
        aria-expanded={expanded}
      >
        {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </button>
      <h3 className="flex-1 truncate text-sm font-medium">{name}</h3>
      {sectionId && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7 flex-shrink-0 text-muted-foreground"
              aria-label={`Section actions for "${name}"`}
            >
              <MoreVertical className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => onEditSection?.(sectionId)}>
              <Pencil className="mr-2 h-3.5 w-3.5" />
              Edit
            </DropdownMenuItem>
            <DropdownMenuItem
              className="text-destructive focus:text-destructive"
              onClick={() => onDeleteSection?.(sectionId)}
            >
              <Trash2 className="mr-2 h-3.5 w-3.5" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

function EntryList({ col, cb }: { col: BoardColumn; cb: ColumnCallbacks }) {
  const { setNodeRef } = useDroppable({ id: itemContainerId(col.personalSectionId) });
  // While searching, show only the entries that match — the column itself is
  // only rendered when it (or a descendant) has a match.
  const entries = cb.searchActive
    ? col.entries.filter((e) => cb.entryMatches(e, col.name))
    : col.entries;
  return (
    <div ref={setNodeRef} className="mt-2 min-h-[8px] space-y-1">
      <SortableContext items={entries.map((e) => e.id)} strategy={verticalListSortingStrategy}>
        {entries.map((entry) => (
          <EntryCard
            key={entry.id}
            entry={entry}
            board={cb.board}
            reviewMode={cb.reviewMode}
            reviewerTargets={cb.reviewerTargets}
            canReview={cb.canReview}
            boardSectionOptions={cb.boardSectionOptions}
            onMoveEntry={cb.onMoveEntry}
            currentColumnId={col.personalSectionId}
            projectById={cb.projectById}
            showProjectChip={cb.showProjectOnTasks}
            // Section view: an ASSIGNED task shows its shared-list section path
            // (its real home); a private item is already in its own column.
            sectionPath={cb.sectionPathByEntryId.get(entry.id)}
            showSectionPath={cb.showSectionPathOnTasks && entry.kind === 'shared'}
            itemKeyPrefix={cb.itemKeyPrefix}
            assignableMembers={cb.assignableMembers}
            selection={cb.selection}
            onEditShared={cb.onEditShared}
            onEditPersonal={cb.onEditPersonal}
          />
        ))}
      </SortableContext>
      {entries.length === 0 && (
        <p className="px-1 py-1 text-xs text-muted-foreground/70">
          {cb.searchActive ? 'No matching tasks' : 'Drop tasks here'}
        </p>
      )}
    </div>
  );
}

function UnsectionedColumnCard({ col, cb }: { col: BoardColumn; cb: ColumnCallbacks }) {
  // The section-less column has no server id; key its persisted collapse state
  // per-list so lists don't share one "Unsectioned" toggle.
  const { expanded, toggle } = useSectionCollapse(`personal-unsectioned:${cb.listId}`);
  return (
    <Card>
      <CardContent className="pt-4">
        <ColumnHeader
          name={col.name}
          expanded={expanded}
          onToggleExpanded={toggle}
          sectionId={null}
        />
        {expanded && <EntryList col={col} cb={cb} />}
      </CardContent>
    </Card>
  );
}

function SectionColumnCard({
  col,
  depth,
  cb,
}: {
  col: BoardColumn;
  depth: number;
  cb: ColumnCallbacks;
}) {
  // Persist this section's collapse state by its stable personal-section id.
  const { expanded, toggle } = useSectionCollapse(col.personalSectionId as string);
  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: col.personalSectionId as string, data: { type: 'section' } });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  const sectionId = col.personalSectionId as string;
  const hasChildren = (cb.childrenByParent.get(sectionId)?.length ?? 0) > 0;
  const dragHandle = (
    <button
      type="button"
      ref={setActivatorNodeRef}
      className="cursor-grab touch-none text-muted-foreground/50 hover:text-muted-foreground"
      aria-label={`Drag section "${col.name}" to reorder`}
      {...attributes}
      {...listeners}
    >
      <GripVertical className="h-4 w-4" />
    </button>
  );

  return (
    <div ref={setNodeRef} style={style}>
      <Card>
        <CardContent className="pt-4">
          <ColumnHeader
            name={col.name}
            expanded={expanded}
            onToggleExpanded={toggle}
            sectionId={sectionId}
            onEditSection={cb.onEditSection}
            onDeleteSection={cb.onDeleteSection}
            dragHandle={dragHandle}
          />
          {expanded && (
            <>
              <EntryList col={col} cb={cb} />
              {hasChildren && (
                <div className="ml-4 mt-3">
                  <SectionTree parentId={sectionId} depth={depth + 1} cb={cb} />
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function EntryCard({
  entry,
  board,
  reviewMode,
  reviewerTargets = [],
  canReview = false,
  boardSectionOptions = [],
  onMoveEntry,
  currentColumnId,
  projectById,
  showProjectChip = true,
  sectionPath,
  showSectionPath = false,
  itemKeyPrefix,
  assignableMembers,
  selection,
  onEditShared,
  onEditPersonal,
}: {
  entry: BoardEntry;
  board: ReturnType<typeof useMyTasksBoard>;
  reviewMode: boolean;
  /** Eligible "send for review" targets; empty hides the action. */
  reviewerTargets?: ReviewerTarget[];
  /** Whether the caller may verify / send back an in-review shared card. */
  canReview?: boolean;
  /** Personal sections (flattened) — the "Move to section" targets. */
  boardSectionOptions?: Array<{ sectionId: string; name: string; depth: number }>;
  /** Move this entry to a personal column (null = Unsectioned). */
  onMoveEntry?: (entry: BoardEntry, personalSectionId: string | null) => void;
  /** The personal column this card currently sits in — checks the current option.
   *  `undefined` (project view) leaves the radio uncontrolled. */
  currentColumnId?: string | null;
  projectById?: Map<string, TodoProjectResponse>;
  /** Section view: show the project chip (gated by the list toggle). */
  showProjectChip?: boolean;
  /** Project view: the entry's section path + whether to show it. */
  sectionPath?: string[];
  showSectionPath?: boolean;
  /** The list's item-id key (e.g. "FR"); shows the KEY-N id on a shared card. */
  itemKeyPrefix?: string;
  /** Workspace roster for the shared-task "Assign to" submenu (empty on personal lists). */
  assignableMembers?: Array<{ principalId: string; label: string }>;
  selection?: BoardSelection;
  onEditShared: (item: TodoItemResponse) => void;
  onEditPersonal: (item: TodoPersonalItemResponse) => void;
}) {
  const project = entry.projectId ? projectById?.get(entry.projectId) : undefined;
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: entry.id,
    data: { type: 'entry' },
  });
  const isCompleted = entry.status === 'completed';
  // On a review-required list a SHARED task's checkbox is a progress marker, not
  // a completion — it never checks a task straight to done (that needs Send for
  // review → Verify from the list view). It reads checked once it's been worked
  // on. Personal items have no review flow, so they keep the plain done toggle.
  // The review progress-marker checkbox applies to top-level shared tasks only —
  // sub-tasks aren't reviewable, so their checkbox is a plain completion toggle
  // (completing them is what unblocks the parent's "Send for review").
  const sharedReview = entry.kind === 'shared' && reviewMode && !entry.shared?.parentItemId;
  // On a review list the shared-task checkbox is the doer's "done" marker
  // (orthogonal to status); everything else is a plain completion toggle.
  const done = sharedReview ? (entry.shared?.done ?? false) : isCompleted;
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };
  const statusStyle = STATUS_STYLES[entry.status] ?? STATUS_STYLES.pending;
  const { toast } = useToast();
  const [subtaskAddActive, setSubtaskAddActive] = useState(false);
  const [subtaskAddTitle, setSubtaskAddTitle] = useState('');
  const subtaskAddRef = useRef<HTMLInputElement>(null);
  // In-flight guard so a double Enter (or Enter + blur) can't fire two identical
  // atomic creates. The ref is the synchronous gate; the state disables the input.
  const subtaskSubmittingRef = useRef(false);
  const [subtaskSubmitting, setSubtaskSubmitting] = useState(false);
  // Suppress the +S completion state/toast if the card unmounts mid-request (e.g.
  // a board refetch removes it) — no toast for a card that's already gone.
  const cardMountedRef = useRef(true);
  useEffect(() => {
    cardMountedRef.current = true;
    return () => {
      cardMountedRef.current = false;
    };
  }, []);
  // "+S" is a shared-card affordance: on a sub-task it makes a SIBLING (same
  // parent); on a top-level shared task it makes a child. It creates a REAL
  // shared sub-task (trackable on the list) and places it here as a movable card.
  // Personal tasks have no "+S".
  const sharedItem =
    entry.kind === 'shared' ? (entry.shared as TodoItemResponse | undefined) : undefined;
  const subtaskParentId = sharedItem ? (sharedItem.parentItemId ?? sharedItem.itemId) : null;
  const canAddSubtask = !!subtaskParentId;

  // Multi-select: highlight when selected; a plain click on the card body selects
  // it (double-click still opens the editor). When part of a MULTI-selection the
  // ⋯ menu acts on the whole selection (bulk verbs, no Edit) — Finder-style.
  const isSelected = selection?.selectedIds.has(entry.id) ?? false;
  const inBulkMode = isSelected && (selection?.count ?? 0) > 1 && !!selection?.bulkActions;

  // Review readiness — mirror the List view (TodoItemRow): a parent can be sent
  // for review only once every sub-task is done/cancelled AND every checklist
  // item is checked. Disables the action with a reason; the server enforces the
  // same precondition (and now returns a clean 400 if it's bypassed).
  const reviewIncompleteSubtasks = (sharedItem?.subtaskTotal ?? 0) > (sharedItem?.subtaskDone ?? 0);
  const reviewIncompleteChecklist =
    (sharedItem?.checklistTotal ?? 0) > (sharedItem?.checklistDone ?? 0);
  // Only a task the doer marked done (the checkbox) can be sent for review.
  const reviewNotDone = !(sharedItem?.done ?? false);
  const reviewBlocked = reviewNotDone || reviewIncompleteSubtasks || reviewIncompleteChecklist;
  const reviewBlockReason = reviewNotDone
    ? 'Check the box to mark it done first'
    : reviewIncompleteSubtasks && reviewIncompleteChecklist
      ? 'Finish every sub-task and check off every checklist item first'
      : reviewIncompleteSubtasks
        ? 'Finish or cancel every sub-task first'
        : 'Check off every checklist item first';

  async function submitSubtask() {
    // Synchronous re-entry gate: a second Enter or a blur while a create is in
    // flight must not fire another identical request.
    if (subtaskSubmittingRef.current) return;
    const title = subtaskAddTitle.trim();
    if (!title || !subtaskParentId) {
      setSubtaskAddActive(false);
      setSubtaskAddTitle('');
      return;
    }
    subtaskSubmittingRef.current = true;
    setSubtaskSubmitting(true);
    try {
      // Await the atomic create+place before clearing, so a failure doesn't
      // silently discard what the user typed.
      await board.createSubtaskOnBoard(subtaskParentId, currentColumnId ?? null, title);
      if (!cardMountedRef.current) return;
      setSubtaskAddActive(false);
      setSubtaskAddTitle('');
    } catch {
      // Keep the input open with its text so the user can retry — unless the card
      // is already gone, in which case there's nothing to keep or toast about.
      if (!cardMountedRef.current) return;
      toast({
        variant: 'destructive',
        title: "Couldn't add the sub-task",
        description: 'Something went wrong. Your text was kept — please try again.',
      });
    } finally {
      subtaskSubmittingRef.current = false;
      setSubtaskSubmitting(false);
    }
  }

  function toggle() {
    if (entry.kind === 'personal') {
      board.setPersonalItemStatus(entry.id, isCompleted ? 'pending' : 'completed');
    } else if (sharedReview) {
      // The checkbox is the doer's "done" marker (orthogonal to status); the
      // server promotes a not-started task to in_progress on check.
      board.setSharedDone(entry.id, !done);
    } else {
      board.setSharedStatus(entry.id, isCompleted ? 'pending' : 'completed');
    }
  }

  // Soft delete, with an Undo toast that restores the item in place.
  function deletePersonal() {
    board.deletePersonalItem(entry.id);
    toast({
      title: 'Task deleted',
      description: entry.title,
      action: (
        <ToastAction altText="Undo delete" onClick={() => board.restorePersonalItem(entry.id)}>
          Undo
        </ToastAction>
      ),
    });
  }

  function setStatus(status: TodoItemStatus) {
    if (entry.kind === 'personal') board.setPersonalItemStatus(entry.id, status);
    else board.setSharedStatus(entry.id, status);
  }

  function setProject(projectId: string | null) {
    if (entry.kind === 'personal') board.updatePersonalItem(entry.id, { projectId });
    else board.updateSharedItem(entry.id, { projectId });
  }
  const projectOptions = projectById ? [...projectById.values()] : [];

  return (
    <>
      <div
        ref={setNodeRef}
        style={style}
        data-todo-row
        className={`group flex items-center gap-2 rounded-md border px-2 py-1.5 transition-colors ${
          isSelected ? 'bg-primary/10 ring-1 ring-primary/40' : 'hover:bg-muted/40'
        }`}
        onClick={(e) => {
          // A single click on the card body selects it (shift = range, cmd/ctrl =
          // toggle); interactive controls do their own thing, and a double-click
          // still opens the editor.
          if (!selection) return;
          const onInteractive = !!(e.target as HTMLElement).closest(
            'button, input, a, [role="button"], [role="checkbox"], [role="menuitem"]',
          );
          if (onInteractive) return;
          e.preventDefault();
          selection.onSelectClick(entry.id, {
            shiftKey: e.shiftKey,
            metaKey: e.metaKey,
            ctrlKey: e.ctrlKey,
          });
        }}
        onDoubleClick={(e) => {
          // Double-click opens the details/edit panel; ignore the interactive
          // controls (drag handle, checkbox, actions menu).
          if (
            (e.target as HTMLElement).closest(
              'button, input, a, [role="button"], [role="checkbox"], [role="menuitem"]',
            )
          )
            return;
          if (entry.kind === 'shared' && entry.shared)
            onEditShared(entry.shared as TodoItemResponse);
          else if (entry.kind === 'personal' && entry.personal)
            onEditPersonal(entry.personal as TodoPersonalItemResponse);
        }}
      >
        <button
          type="button"
          className="cursor-grab touch-none text-muted-foreground/50 hover:text-muted-foreground"
          aria-label="Drag to arrange"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <Checkbox
          checked={done}
          onCheckedChange={toggle}
          aria-label={`Mark "${entry.title}" ${done ? 'not done' : 'done'}`}
        />
        {/* Task id (KEY-N) next to the checkbox, mirroring the List view. Only a
            SHARED task has a list seq; a private card has no id (it shows the
            "Private" badge instead). */}
        {itemKeyPrefix && entry.kind === 'shared' && entry.shared && (
          <code
            className="flex-shrink-0 font-mono text-[10px] text-muted-foreground"
            title="Task id"
          >
            {formatItemId(itemKeyPrefix, entry.shared.seq)}
          </code>
        )}
        <span
          className={`min-w-0 flex-1 break-words text-sm ${isCompleted ? 'line-through text-muted-foreground' : ''}`}
        >
          {entry.title}
        </span>
        {entry.kind === 'personal' && (
          <Badge
            variant="outline"
            className="flex-shrink-0 gap-1 px-1.5 py-0 text-[10px] text-muted-foreground"
            title="Private — only you can see this task; it isn’t on the shared list"
          >
            <Lock className="h-2.5 w-2.5" />
            Private
          </Badge>
        )}
        {entry.checklistTotal > 0 && (
          <Badge
            variant="outline"
            className="flex-shrink-0 gap-1 px-1.5 py-0 text-[10px] text-muted-foreground"
            title={`${entry.checklistDone} of ${entry.checklistTotal} checklist items done`}
          >
            <ListChecks className="h-2.5 w-2.5" />
            {entry.checklistDone}/{entry.checklistTotal}
          </Badge>
        )}
        {/* Section path + project chip are independent — the section view shows
          both (its columns are personal, so the shared-list section is useful
          context); the project view shows only the section path. */}
        {showSectionPath && <SectionPathChip path={sectionPath ?? []} />}
        {showProjectChip && project && <ProjectChip project={project} />}
        {entry.status !== 'pending' && (
          <Badge
            variant="secondary"
            className={`flex-shrink-0 border-0 px-1.5 py-0 text-[10px] ${statusStyle.className}`}
          >
            {statusStyle.label}
          </Badge>
        )}

        {canAddSubtask && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 flex-shrink-0 text-muted-foreground opacity-0 transition-opacity hover:text-foreground group-hover:opacity-100 focus-visible:opacity-100"
            onClick={(e) => {
              e.stopPropagation();
              setSubtaskAddActive(true);
              setTimeout(() => subtaskAddRef.current?.focus(), 0);
            }}
            aria-label={
              sharedItem?.parentItemId
                ? `Add a sibling sub-task to "${entry.title}"`
                : `Add a sub-task to "${entry.title}"`
            }
            title={sharedItem?.parentItemId ? 'Add a sibling sub-task' : 'Add a sub-task'}
          >
            <ListPlus className="h-3.5 w-3.5" />
          </Button>
        )}

        {/* Every task — personal or an assigned shared task — is fully manageable
          from its own actions menu. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6 flex-shrink-0 text-muted-foreground opacity-60 transition-opacity group-hover:opacity-100"
              aria-label={`Actions for "${entry.title}"`}
            >
              <MoreVertical className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {inBulkMode ? (
              <BulkActionsMenuItems {...selection!.bulkActions} />
            ) : (
              <>
                {entry.kind === 'shared' && entry.shared && (
                  <DropdownMenuItem onClick={() => onEditShared(entry.shared as TodoItemResponse)}>
                    <Pencil className="mr-2 h-3.5 w-3.5" />
                    Edit
                  </DropdownMenuItem>
                )}
                {entry.kind === 'personal' && entry.personal && (
                  <DropdownMenuItem
                    onClick={() => onEditPersonal(entry.personal as TodoPersonalItemResponse)}
                  >
                    <Pencil className="mr-2 h-3.5 w-3.5" />
                    Edit
                  </DropdownMenuItem>
                )}
                <DropdownMenuSub>
                  <DropdownMenuSubTrigger>
                    <CircleDot className="mr-2 h-3.5 w-3.5" />
                    Set status
                  </DropdownMenuSubTrigger>
                  <DropdownMenuSubContent>
                    <DropdownMenuRadioGroup
                      value={entry.status}
                      onValueChange={(v) => setStatus(v as TodoItemStatus)}
                    >
                      {WORK_STATUSES.map((s) => (
                        <DropdownMenuRadioItem key={s.value} value={s.value}>
                          {s.label}
                        </DropdownMenuRadioItem>
                      ))}
                    </DropdownMenuRadioGroup>
                  </DropdownMenuSubContent>
                </DropdownMenuSub>
                {projectOptions.length > 0 && (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <FolderKanban className="mr-2 h-3.5 w-3.5" />
                      Set project
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      <DropdownMenuRadioGroup
                        value={entry.projectId ?? ''}
                        onValueChange={(v) => setProject(v === '' ? null : v)}
                      >
                        <DropdownMenuRadioItem value="">No project</DropdownMenuRadioItem>
                        {projectOptions.map((p) => (
                          <DropdownMenuRadioItem key={p.projectId} value={p.projectId}>
                            <span
                              className={`mr-2 h-2 w-2 flex-shrink-0 rounded-full ${resolveProjectColor(p.color).dot}`}
                            />
                            {p.name}
                          </DropdownMenuRadioItem>
                        ))}
                      </DropdownMenuRadioGroup>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                )}
                {/* Assign to — hand this shared task off to another member (or clear
              the assignee). The board only shows tasks assigned to the caller, so
              the server allows the reassignment; a task handed off leaves the
              board. Personal cards have no shared assignee, so it's shared-only. */}
                {entry.kind === 'shared' && assignableMembers && assignableMembers.length > 0 && (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <UserRound className="mr-2 h-3.5 w-3.5" />
                      Assign to
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent>
                      <DropdownMenuRadioGroup
                        value={sharedItem?.assignedToPrincipalId ?? ''}
                        onValueChange={(v) => {
                          if (v === '') board.unassignShared(entry.id);
                          else board.assignShared(entry.id, v);
                        }}
                      >
                        <DropdownMenuRadioItem value="">Unassigned</DropdownMenuRadioItem>
                        {assignableMembers.map((m) => (
                          <DropdownMenuRadioItem key={m.principalId} value={m.principalId}>
                            {m.label}
                          </DropdownMenuRadioItem>
                        ))}
                      </DropdownMenuRadioGroup>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                )}
                {/* Send an assigned shared task for review (review-mode lists only) —
              mirrors the List view. Pick one reviewer, or broadcast to all. When
              blocked (sub-tasks/checklist unfinished, or not marked done), render a
              plain DISABLED item — a disabled sub-trigger doesn't gray out, so the
              List view swaps to this too. */}
                {entry.kind === 'shared' &&
                  !sharedItem?.parentItemId &&
                  reviewMode &&
                  reviewerTargets.length > 0 &&
                  (reviewBlocked ? (
                    <DropdownMenuItem
                      disabled
                      title={reviewBlockReason}
                      className="text-muted-foreground"
                    >
                      <Eye className="mr-2 h-3.5 w-3.5" />
                      Send for review
                    </DropdownMenuItem>
                  ) : (
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger>
                        <Eye className="mr-2 h-3.5 w-3.5" />
                        Send for review
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent>
                        <DropdownMenuItem onClick={() => board.requestSharedReview(entry.id, null)}>
                          All reviewers
                        </DropdownMenuItem>
                        {reviewerTargets.map((t) => (
                          <DropdownMenuItem
                            key={t.principalId}
                            onClick={() => board.requestSharedReview(entry.id, t.principalId)}
                          >
                            {t.label}
                          </DropdownMenuItem>
                        ))}
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                  ))}
                {onMoveEntry && boardSectionOptions.length > 0 && (
                  <DropdownMenuSub>
                    <DropdownMenuSubTrigger>
                      <FolderInput className="mr-2 h-3.5 w-3.5" />
                      Move to section
                    </DropdownMenuSubTrigger>
                    <DropdownMenuSubContent className="max-h-72 overflow-y-auto">
                      <DropdownMenuRadioGroup
                        value={currentColumnId === undefined ? undefined : (currentColumnId ?? '')}
                        onValueChange={(v) => onMoveEntry(entry, v === '' ? null : v)}
                      >
                        <DropdownMenuRadioItem value="">Unsectioned</DropdownMenuRadioItem>
                        {boardSectionOptions.map((s) => (
                          <DropdownMenuRadioItem key={s.sectionId} value={s.sectionId}>
                            <span style={{ paddingLeft: `${s.depth * 0.75}rem` }}>{s.name}</span>
                          </DropdownMenuRadioItem>
                        ))}
                      </DropdownMenuRadioGroup>
                    </DropdownMenuSubContent>
                  </DropdownMenuSub>
                )}
                {entry.kind === 'personal' && (
                  <DropdownMenuItem
                    className="text-destructive focus:text-destructive"
                    onClick={deletePersonal}
                  >
                    <Trash2 className="mr-2 h-3.5 w-3.5" />
                    Delete
                  </DropdownMenuItem>
                )}
              </>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {/* Reviewer affordances on an in-review shared card — mirrors the List
          view (TodoItemRow). A manager or a designated list reviewer can Verify
          (→ completed) or Send back (→ in progress, clears done). */}
      {entry.kind === 'shared' &&
        !sharedItem?.parentItemId &&
        reviewMode &&
        sharedItem?.status === 'in_review' &&
        canReview && (
          <div className="ml-6 mt-1 mb-1 flex flex-wrap items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1 px-2 text-xs text-green-700 hover:bg-green-100 hover:text-green-800 dark:text-green-400 dark:hover:bg-green-900/30"
              onClick={() => board.verifyShared(entry.id)}
              title="Verify — mark this task as done"
            >
              <CheckCircle2 className="h-3.5 w-3.5" />
              Verify
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 gap-1 px-2 text-xs text-muted-foreground"
              onClick={() => board.sendBackShared(entry.id)}
              title="Send back to in progress"
            >
              <Undo2 className="h-3.5 w-3.5" />
              Send back
            </Button>
          </div>
        )}
      {subtaskAddActive && (
        <div className="ml-6 mt-1 mb-1">
          <Input
            ref={subtaskAddRef}
            value={subtaskAddTitle}
            onChange={(e) => setSubtaskAddTitle(e.target.value)}
            placeholder={sharedItem?.parentItemId ? 'New sibling sub-task...' : 'New sub-task...'}
            className="h-8 text-sm"
            disabled={subtaskSubmitting}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                submitSubtask();
              }
              if (e.key === 'Escape') {
                setSubtaskAddActive(false);
                setSubtaskAddTitle('');
              }
            }}
            onBlur={submitSubtask}
          />
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Project-grouped board view — the same board tasks (private + assigned)
// grouped by PROJECT instead of section. Each project (plus a "No project"
// bucket) is a collapsible group and a drop target: dragging a task into
// another group reassigns its project (via the personal / shared update path).
// ---------------------------------------------------------------------------

const NO_PROJECT = '__none__';

function BoardProjectView({
  entries,
  projects,
  projectById,
  board,
  reviewMode,
  reviewerTargets,
  canReview,
  boardSectionOptions,
  onMoveEntry,
  sectionPathByEntryId,
  showSectionPath,
  itemKeyPrefix,
  assignableMembers,
  selection,
  onEditShared,
  onEditPersonal,
}: {
  entries: BoardEntry[];
  projects: TodoProjectResponse[];
  projectById: Map<string, TodoProjectResponse>;
  board: ReturnType<typeof useMyTasksBoard>;
  reviewMode: boolean;
  reviewerTargets: ReviewerTarget[];
  canReview: boolean;
  boardSectionOptions: Array<{ sectionId: string; name: string; depth: number }>;
  onMoveEntry: (entry: BoardEntry, personalSectionId: string | null) => void;
  sectionPathByEntryId: Map<string, string[]>;
  showSectionPath: boolean;
  itemKeyPrefix?: string;
  /** Workspace roster for the shared-task "Assign to" submenu (empty on personal lists). */
  assignableMembers?: Array<{ principalId: string; label: string }>;
  selection?: BoardSelection;
  onEditShared: (item: TodoItemResponse) => void;
  onEditPersonal: (item: TodoPersonalItemResponse) => void;
}) {
  const [activeEntry, setActiveEntry] = useState<BoardEntry | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  const byProject = new Map<string, BoardEntry[]>();
  byProject.set(NO_PROJECT, []);
  for (const p of projects) byProject.set(p.projectId, []);
  for (const e of entries) {
    const key = e.projectId && byProject.has(e.projectId) ? e.projectId : NO_PROJECT;
    byProject.get(key)!.push(e);
  }
  const projectOfEntry = (id: string): string => {
    const e = entries.find((x) => x.id === id);
    return e?.projectId && byProject.has(e.projectId) ? e.projectId : NO_PROJECT;
  };

  function handleDragEnd(e: DragEndEvent) {
    setActiveEntry(null);
    const { active, over } = e;
    if (!over) return;
    const id = String(active.id);
    const overId = String(over.id);
    const targetKey = overId.startsWith('projgrp:')
      ? overId.slice('projgrp:'.length)
      : projectOfEntry(overId);
    if (!targetKey) return;
    const projectId = targetKey === NO_PROJECT ? null : targetKey;
    // Multi-drag: dropping a SELECTED card re-projects the WHOLE selection, matching
    // the section board's group-move semantics (project grouping is a valid
    // multi-select surface — moving only the dragged card would silently drop the
    // rest). A single (or unselected) drag moves just that card.
    const isMultiDrag = !!selection && selection.count > 1 && selection.selectedIds.has(id);
    const selected = isMultiDrag
      ? entries.filter((x) => selection!.selectedIds.has(x.id))
      : entries.filter((x) => x.id === id);
    // Skip cards already in the target project (self-drop / no-op writes).
    const targets = selected.filter((t) => projectOfEntry(t.id) !== targetKey);
    if (targets.length === 0) return;
    for (const t of targets) {
      if (t.kind === 'personal') board.updatePersonalItem(t.id, { projectId });
      else board.updateSharedItem(t.id, { projectId });
    }
    if (isMultiDrag) selection!.clear();
  }

  const groups: Array<{ key: string; label: string; color: string | null }> = [
    { key: NO_PROJECT, label: 'No project', color: null },
    ...projects.map((p) => ({ key: p.projectId, label: p.name, color: p.color })),
  ];

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={(e: DragStartEvent) =>
        setActiveEntry(entries.find((x) => x.id === String(e.active.id)) ?? null)
      }
      onDragEnd={handleDragEnd}
      onDragCancel={() => setActiveEntry(null)}
    >
      <div className="space-y-3">
        {groups.map((g) => (
          <BoardProjectGroup
            key={g.key}
            groupKey={g.key}
            label={g.label}
            color={g.color}
            entries={byProject.get(g.key) ?? []}
            board={board}
            reviewMode={reviewMode}
            reviewerTargets={reviewerTargets}
            canReview={canReview}
            boardSectionOptions={boardSectionOptions}
            onMoveEntry={onMoveEntry}
            projectById={projectById}
            sectionPathByEntryId={sectionPathByEntryId}
            showSectionPath={showSectionPath}
            itemKeyPrefix={itemKeyPrefix}
            assignableMembers={assignableMembers}
            selection={selection}
            onEditShared={onEditShared}
            onEditPersonal={onEditPersonal}
          />
        ))}
        {entries.length === 0 && (
          <p className="py-10 text-center text-sm text-muted-foreground">
            No tasks yet. Add tasks and assign them to a project.
          </p>
        )}
      </div>
      <DragOverlay>
        {activeEntry ? (
          <div className="rounded-md border bg-background px-3 py-2 text-sm shadow-lg">
            {activeEntry.title}
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

function BoardProjectGroup({
  groupKey,
  label,
  color,
  entries,
  board,
  reviewMode,
  reviewerTargets,
  canReview,
  boardSectionOptions,
  onMoveEntry,
  projectById,
  sectionPathByEntryId,
  showSectionPath,
  itemKeyPrefix,
  assignableMembers,
  selection,
  onEditShared,
  onEditPersonal,
}: {
  groupKey: string;
  label: string;
  color: string | null;
  entries: BoardEntry[];
  board: ReturnType<typeof useMyTasksBoard>;
  reviewMode: boolean;
  reviewerTargets: ReviewerTarget[];
  canReview: boolean;
  boardSectionOptions: Array<{ sectionId: string; name: string; depth: number }>;
  onMoveEntry: (entry: BoardEntry, personalSectionId: string | null) => void;
  projectById: Map<string, TodoProjectResponse>;
  sectionPathByEntryId: Map<string, string[]>;
  showSectionPath: boolean;
  itemKeyPrefix?: string;
  /** Workspace roster for the shared-task "Assign to" submenu (empty on personal lists). */
  assignableMembers?: Array<{ principalId: string; label: string }>;
  selection?: BoardSelection;
  onEditShared: (item: TodoItemResponse) => void;
  onEditPersonal: (item: TodoPersonalItemResponse) => void;
}) {
  const { expanded, toggle } = useSectionCollapse(`board-project:${groupKey}`);
  const { setNodeRef, isOver } = useDroppable({ id: `projgrp:${groupKey}` });
  const dot = resolveProjectColor(color);
  return (
    <Card className={isOver ? 'border-primary' : undefined}>
      <CardContent className="pt-4">
        <button
          type="button"
          onClick={toggle}
          className="flex w-full items-center gap-1.5 text-left"
          aria-expanded={expanded}
          aria-label={`${expanded ? 'Collapse' : 'Expand'} project "${label}"`}
        >
          {expanded ? (
            <ChevronDown className="h-4 w-4 flex-shrink-0" />
          ) : (
            <ChevronRight className="h-4 w-4 flex-shrink-0" />
          )}
          {groupKey !== NO_PROJECT && (
            <span className={`h-2.5 w-2.5 flex-shrink-0 rounded-full ${dot.dot}`} />
          )}
          <h3 className="flex-1 truncate text-sm font-medium">{label}</h3>
          <span className="text-xs text-muted-foreground/60">{entries.length}</span>
        </button>
        {expanded && (
          <div ref={setNodeRef} className="mt-2 min-h-[8px] space-y-1">
            <SortableContext
              items={entries.map((e) => e.id)}
              strategy={verticalListSortingStrategy}
            >
              {entries.map((entry) => (
                <EntryCard
                  key={entry.id}
                  entry={entry}
                  board={board}
                  reviewMode={reviewMode}
                  reviewerTargets={reviewerTargets}
                  canReview={canReview}
                  boardSectionOptions={boardSectionOptions}
                  onMoveEntry={onMoveEntry}
                  projectById={projectById}
                  showProjectChip={false}
                  sectionPath={sectionPathByEntryId.get(entry.id)}
                  showSectionPath={showSectionPath}
                  itemKeyPrefix={itemKeyPrefix}
                  assignableMembers={assignableMembers}
                  selection={selection}
                  onEditShared={onEditShared}
                  onEditPersonal={onEditPersonal}
                />
              ))}
            </SortableContext>
            {entries.length === 0 && (
              <p className="px-1 py-1 text-xs text-muted-foreground/70">Drop tasks here</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
